import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  ListGroup,
  Button,
  Table,
} from "react-bootstrap";
import {
  getLevelChipClass,
  getLevelLetterFromId,
  getLevelLettersFromIds,
} from "../utils/levelColors";

const API_URL = process.env.REACT_APP_API_URL;

const IndividualTournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [newPlayers, setNewPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);

  const getLevelName = (levelId) => getLevelLetterFromId(levelId) || "N/A";

  const handlePrevYear = () => {
    const currentIndex = availableYears.indexOf(parseInt(selectedYear));
    if (currentIndex < availableYears.length - 1) {
      setSelectedYear(availableYears[currentIndex + 1].toString());
    }
  };

  const handleNextYear = () => {
    const currentIndex = availableYears.indexOf(parseInt(selectedYear));
    if (currentIndex > 0) {
      setSelectedYear(availableYears[currentIndex - 1].toString());
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchTournaments = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/tournaments/all-json`);

        const allTournaments = response.data;
        setTournaments(allTournaments);

        const years = [
          ...new Set(
            allTournaments
              .map((tournament) =>
                tournament.created_at
                  ? new Date(tournament.created_at).getFullYear()
                  : null,
              )
              .filter((year) => year !== null),
          ),
        ].sort((a, b) => b - a);

        setAvailableYears(years);

        if (years.length > 0) {
          setSelectedYear(years[0].toString());
        }
      } catch (err) {
        console.error("Error fetching tournaments:", err);
        if (isMounted) {
          setError("Không thể tải dữ liệu giải đấu.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTournaments();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchNewPlayers = async () => {
      if (!selectedYear) return;

      try {
        setLoadingPlayers(true);

        const response = await axios.get(`${API_URL}/playerspublic`);

        if (!isMounted) return;

        const allPlayers = response.data.players || response.data.data || response.data || [];

        const filtered = allPlayers.filter((player) => {
          if (!player.created_at) return false;

          const createdDate = new Date(player.created_at);
          const year = createdDate.getFullYear();
          const month = createdDate.getMonth() + 1;
          const day = createdDate.getDate();

          if (year !== parseInt(selectedYear)) return false;

          const inOctober = month === 10;
          const inNovember = month === 11;
          const inDecemberUpTo30 = month === 12 && day <= 30;

          return inOctober || inNovember || inDecemberUpTo30;
        });

        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        setNewPlayers(filtered);
      } catch (err) {
        console.error("Error fetching new players:", err);
      } finally {
        if (isMounted) {
          setLoadingPlayers(false);
        }
      }
    };

    fetchNewPlayers();

    return () => {
      isMounted = false;
    };
  }, [selectedYear]);

  const filteredTournaments = useMemo(() => {
    if (!selectedYear) return tournaments;

    return tournaments.filter((tournament) => {
      const tournamentYear = new Date(tournament.created_at)
        .getFullYear()
        .toString();
      return tournamentYear === selectedYear;
    });
  }, [tournaments, selectedYear]);

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Đang tải...</span>
        </Spinner>
        <p className="mt-3">Đang tải danh sách giải đấu...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger" className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="mb-4">
        <h1 className="fw-bold mb-1">Giải Đấu Cá Nhân</h1>
        <p className="text-muted small">Thông tin các giải đấu đơn và đôi dành cho cá nhân</p>
      </div>

      {/* Year Selector */}
      {availableYears.length > 0 && (
        <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
          <Card.Body className="text-start py-4 px-4 px-md-5">
            <p className="text-muted small text-uppercase fw-bold mb-3">Chọn Năm</p>
            <div className="d-flex justify-content-center align-items-center gap-4">
              <Button
                variant="link"
                className="text-decoration-none p-0 text-dark fs-3"
                onClick={handlePrevYear}
                disabled={
                  availableYears.indexOf(parseInt(selectedYear)) ===
                  availableYears.length - 1
                }
              >
                ‹
              </Button>
              <h2 className="mb-0 fw-bold">
                {selectedYear}
              </h2>
              <Button
                variant="link"
                className="text-decoration-none p-0 text-dark fs-3"
                onClick={handleNextYear}
                disabled={
                  availableYears.indexOf(parseInt(selectedYear)) === 0
                }
              >
                ›
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Stats Cards */}
      <Row className="mb-4 g-3">
        <Col md={4} xs={12}>
          <Card className="text-start border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-4 px-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Tổng Giải Đấu</p>
              <h2 className="fw-bold mb-0">
                {tournaments.length}
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} xs={12}>
          <Card className="text-start border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-4 px-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Giải Đấu Năm {selectedYear}</p>
              <h2 className="fw-bold mb-0">
                {filteredTournaments.length}
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} xs={12}>
          <Card className="text-start border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-4 px-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Tổng Đăng Ký</p>
              <h2 className="fw-bold mb-0">
                {filteredTournaments.reduce(
                  (sum, t) => sum + (t.registrations?.length || 0),
                  0,
                )}
              </h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tournament Cards */}
      {filteredTournaments.length > 0 ? (
        <Row className="g-3 justify-content-center">
          {filteredTournaments.map((tournament) => {
            const registeredCount = tournament.registrations?.length || 0;
            const isFull = registeredCount >= tournament.player_limit;

            return (
              <Col md={6} lg={3} xs={12} key={tournament.id}>
                <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <Card.Body className="d-flex flex-column p-4">
                    <h5 className="fw-bold mb-3">
                      {tournament.name}
                    </h5>
                    <ListGroup variant="flush" className="flex-grow-1">
                      <ListGroup.Item className="border-0 px-0 py-2 d-flex justify-content-between">
                        <span className="text-muted small text-uppercase fw-bold">Thể Loại</span>
                        <span className="fw-semibold">
                          {tournament.type === "single" ? "Đơn" : "Đôi"}
                        </span>
                      </ListGroup.Item>
                      <ListGroup.Item className="border-0 px-0 py-2 d-flex justify-content-between">
                        <span className="text-muted small text-uppercase fw-bold">Cấp Độ</span>
                        <span className="d-flex flex-wrap gap-1 justify-content-end">
                          {getLevelLettersFromIds(tournament.level_ids).map((letter) => (
                            <span key={letter} className={getLevelChipClass(letter)}>
                              {letter}
                            </span>
                          ))}
                        </span>
                      </ListGroup.Item>
                      {tournament.start_date && (
                        <ListGroup.Item className="border-0 px-0 py-2 d-flex justify-content-between">
                          <span className="text-muted small text-uppercase fw-bold">Thời gian</span>
                          <span className="small">
                            {new Date(tournament.start_date).toLocaleDateString("vi-VN")}
                          </span>
                        </ListGroup.Item>
                      )}
                      {tournament.location && (
                        <ListGroup.Item className="border-0 px-0 py-2 d-flex flex-column">
                          <span className="text-muted small text-uppercase fw-bold mb-1">Địa điểm</span>
                          <span className="small">{tournament.location}</span>
                        </ListGroup.Item>
                      )}
                      <ListGroup.Item className="border-0 px-0 py-2 d-flex justify-content-between">
                        <span className="text-muted small text-uppercase fw-bold">Giới Hạn</span>
                        <span className="fw-bold">
                          {tournament.player_limit}
                        </span>
                      </ListGroup.Item>
                      <ListGroup.Item className="border-0 px-0 py-2 d-flex justify-content-between align-items-center">
                        <span className="text-muted small text-uppercase fw-bold">Đăng Ký</span>
                        <span
                          className={`fw-bold ${isFull ? "text-danger" : "text-success"}`}
                        >
                          {registeredCount} / {tournament.player_limit}
                        </span>
                      </ListGroup.Item>
                      <ListGroup.Item className="border-0 px-0 py-2">
                        <strong>Ngày Tạo:</strong>{" "}
                        <small>
                          {new Date(tournament.created_at).toLocaleDateString("vi-VN")}
                        </small>
                      </ListGroup.Item>
                    </ListGroup>

                    {tournament.id && (
                      <>
                        {tournament.status === 'draw_generated' ? (
                          <Button
                            as={Link}
                            to={`/tournament-schedule/${tournament.id}`}
                            variant="success"
                            className="mt-3 w-100"
                            style={{ borderRadius: '8px' }}
                          >
                            Lịch thi đấu
                          </Button>
                        ) : (
                          <Button
                            variant={isFull ? "secondary" : "primary"}
                            className="mt-3 w-100"
                            href={`/register-tournament/${tournament.id}`}
                            style={{ borderRadius: '8px' }}
                          >
                            {isFull ? "Danh Sách ĐK" : "Đăng ký"}
                          </Button>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Alert variant="info" className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
          {selectedYear
            ? `Không tìm thấy giải đấu cá nhân nào cho năm ${selectedYear}.`
            : "Không tìm thấy giải đấu cá nhân nào."}
        </Alert>
      )}

      {/* New Players Section */}
      <Row className="mt-5 mb-4">
        <Col>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <Card.Header
              className="bg-white py-4 px-4 border-bottom"
            >
              <h4 className="mb-0 fw-bold">
                Vận Động Viên Mới (Tháng 10 - 30/12/{selectedYear})
              </h4>
            </Card.Header>
            <Card.Body className="p-0">
              {loadingPlayers ? (
                <div className="text-center py-5">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Đang tải danh sách VĐV mới...</span>
                </div>
              ) : newPlayers.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="bg-white border-bottom">
                        <tr>
                          <th className="text-center" style={{ width: "5%" }}>
                            STT
                          </th>
                          <th style={{ width: "8%" }}>Ảnh</th>
                          <th style={{ width: "25%" }}>Họ Tên</th>
                          <th className="text-center" style={{ width: "12%" }}>
                            Ngày Sinh
                          </th>
                          <th className="text-center" style={{ width: "10%" }}>
                            Hạng
                          </th>
                          <th className="text-center" style={{ width: "10%" }}>
                            Điểm Elo
                          </th>
                          <th className="text-center" style={{ width: "15%" }}>
                            Ngày Tạo
                          </th>
                          <th className="text-center" style={{ width: "15%" }}>
                            Trạng Thái
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {newPlayers.map((player, index) => (
                          <tr key={player.id}>
                            <td className="text-center fw-bold">{index + 1}</td>
                            <td>
                              <div className="d-flex justify-content-center align-items-center">
                                {player.image ? (
                                  <img
                                    src={`${API_URL}/uploads/${player.image}`}
                                    alt={player.name}
                                    className="rounded-circle"
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      objectFit: "cover",
                                      border: "3px solid #fff",
                                      boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
                                    }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src =
                                        "https://via.placeholder.com/50?text=No+Image";
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      fontSize: "1.25rem",
                                      fontWeight: "bold",
                                      backgroundColor: "var(--primary-color)",
                                      boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
                                    }}
                                  >
                                    {player.name?.charAt(0) || "?"}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              {player.id ? (
                                <Link
                                  to={`/players/${player.id}`}
                                  style={{
                                    textDecoration: 'none',
                                    fontWeight: '600',
                                    color: 'inherit',
                                    transition: 'color 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-color)'}
                                  onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                                >
                                  {player.name}
                                </Link>
                              ) : (
                                <strong>{player.name}</strong>
                              )}
                            </td>
                            <td className="text-center">
                              <small>
                                {player.date_of_birth
                                  ? new Date(
                                    player.date_of_birth,
                                  ).toLocaleDateString("vi-VN")
                                  : "N/A"}
                              </small>
                            </td>
                            <td className="text-center">
                              <span className={`${getLevelChipClass(getLevelName(player.level_present))} level-chip-lg`}>
                                {getLevelName(player.level_present)}
                              </span>
                            </td>
                            <td className="text-center fw-bold">
                              {player.level_point
                                ? parseFloat(player.level_point).toFixed(0)
                                : "0"}
                            </td>
                            <td className="text-center">
                              <small className="text-muted">
                                {new Date(player.created_at).toLocaleDateString(
                                  "vi-VN",
                                  {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  },
                                )}
                                <br />
                                <span className="text-secondary">
                                  {new Date(player.created_at).toLocaleTimeString(
                                    "vi-VN",
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    },
                                  )}
                                </span>
                              </small>
                            </td>
                            <td className="text-center">
                              {player.status === 1 ? (
                                <span className="text-success small fw-bold">
                                  Đã Duyệt
                                </span>
                              ) : (
                                <span className="text-warning small fw-bold">
                                  Chờ Duyệt
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <div className="p-3 text-end bg-light">
                    <span
                      className="text-muted small text-uppercase fw-bold"
                    >
                      Tổng: {newPlayers.length} VĐV
                    </span>
                  </div>
                </>
              ) : (
                <Alert variant="info" className="m-4 border-0" style={{ borderRadius: '12px' }}>
                  Không có vận động viên mới nào được thêm trong khoảng thời
                  gian từ 1/10 đến 30/12/{selectedYear}.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default IndividualTournaments;
